import { alpha, Box, Stack, styled, Typography } from '@mui/material';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type ViewProductStore from '../../AppData/_stores/ViewProductStore';
import ErrorBoundary from '../../Common/_components/ErrorBoundary';
import { useAppSelector } from '../../Common/_hooks/useAppSelector';
import useIsSaleProduct, { SALE_DISCOUNT_PERCENT } from '../../Common/_hooks/useIsSaleProduct';
import { useRecentlyViewedProducts } from '../../Product/recentlyViewedProducts';
import ProductCatalogCardImage from '../Card/ProductCatalogCardImage';

const MAX_RECENT_PRODUCTS_SHOWN = 6;
export const MAX_MOBILE_RECENT_PRODUCTS_SHOWN = 4;

const RecentlyViewedSection = styled(Box)(({ theme }) => ({
  borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
  paddingBottom: theme.spacing(3),

  [theme.breakpoints.down('sm')]: {
    paddingBottom: theme.spacing(2),
  },
}));

const RecentlyViewedHeader = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  flexDirection: 'row',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1),
}));

const RecentlyViewedProductsList = styled(Stack)(({ theme }) => ({
  [theme.breakpoints.down('xl')]: {
    '& > :nth-of-type(n + 6)': {
      display: 'none',
    },
    '& > :nth-of-type(n + 5)': {
      display: 'none',
    },
  },

  [theme.breakpoints.down('lg')]: {
    '& > :nth-of-type(n + 4)': {
      display: 'none',
    },
  },

  [theme.breakpoints.down('md')]: {
    '& > :nth-of-type(n + 3)': {
      display: 'none',
    },
  },

  [theme.breakpoints.down('sm')]: {
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    marginTop: theme.spacing(-1),
    marginBottom: theme.spacing(-1),
    scrollSnapType: 'x proximity',
    scrollbarWidth: 'thin',

    '& > :nth-of-type(n + 3)': {
      display: 'flex',
    },
  },
}));

const RecentlyViewedProductLink = styled(Link)(({ theme }) => ({
  alignItems: 'center',
  background: '#fff',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  borderRadius: 8,
  color: theme.palette.text.primary,
  display: 'flex',
  minHeight: 72,
  overflow: 'hidden',
  textDecoration: 'none',
  transition: 'border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease',
  flex: 1,
  minWidth: 0,
  maxWidth: 300,

  '&:hover': {
    borderColor: alpha(theme.palette.accent.main, 0.36),
    boxShadow: '0 8px 18px rgba(0, 0, 0, 0.06)',
    textDecoration: 'none',
    transform: 'translateY(-1px)',
  },

  [theme.breakpoints.down('sm')]: {
    flex: '0 0 min(280px, calc(100vw - 32px))',
    maxWidth: 'none',
    scrollSnapAlign: 'start',
  },
}));

const RecentlyViewedImage = styled('div')(({ theme }) => ({
  background: alpha(theme.palette.secondary.main, 0.06),
  width: 72,
  height: 72,
  flexShrink: 0,
  overflow: 'hidden',
  position: 'relative',

  'img': {
    display: 'block',
    height: '100%',
    objectFit: 'cover',
    width: '100%',
  },
}));

const RecentlyViewedProductName = styled(Typography)({
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  lineClamp: 2,
  WebkitLineClamp: 2,
  lineHeight: '1.3',
});

const RecentlyViewedPrimaryPrice = styled(Stack)({
  minHeight: 21,
});

const RecentlyViewedRegularPrice = styled('span')(({ theme }) => ({
  color: theme.palette.text.disabled,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0,
  lineHeight: 1,
  textDecoration: 'line-through',
  textDecorationThickness: '1.25px',
  whiteSpace: 'nowrap',
}));

const RecentlyViewedSalePrice = styled('span')(({ theme }) => ({
  color: theme.palette.accent.main,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0,
  lineHeight: 1,
  whiteSpace: 'nowrap',
}));

interface RecentlyViewedProductsProps {
  maxProductsShown?: number
}

function RecentlyViewedProducts(props: RecentlyViewedProductsProps) {
  const [t] = useTranslation('products');
  const [tR] = useTranslation('routes');
  const isSaleProduct = useIsSaleProduct();
  const viewProducts = useAppSelector(state => state.get('appData').get('viewProducts'));
  const initialRecentProductIds = useAppSelector(state => state.get('UIData').get('recentlyViewedProductIds'));
  const initialRecentProductIdList = useMemo(() => initialRecentProductIds.toArray(), [initialRecentProductIds]);
  const {
    addRecentlyViewedProduct,
    recentlyViewedProductIds: recentProductIds,
    refreshRecentlyViewedProducts,
  } = useRecentlyViewedProducts(initialRecentProductIdList);

  useEffect(() => {
    window.addEventListener('focus', refreshRecentlyViewedProducts);

    return () => {
      window.removeEventListener('focus', refreshRecentlyViewedProducts);
    }
  }, [refreshRecentlyViewedProducts]);

  const recentProducts = useMemo(() => {
    const products: ViewProductStore[] = [];

    recentProductIds.forEach(productId => {
      const product = viewProducts.get(productId);
      if (product !== undefined) {
        products.push(product);
      }
    });

    return products.slice(0, props.maxProductsShown ?? MAX_RECENT_PRODUCTS_SHOWN);
  }, [props.maxProductsShown, recentProductIds, viewProducts]);

  const onProductClick = useCallback((product: ViewProductStore) => {
    addRecentlyViewedProduct(product.get('id'));
  }, [addRecentlyViewedProduct]);

  if (recentProducts.length === 0) return null;

  return <RecentlyViewedSection component="section">
    <RecentlyViewedHeader>
      <Typography variant="h4">{t('Recently viewed')}</Typography>
    </RecentlyViewedHeader>
    <RecentlyViewedProductsList direction="row" spacing={1}>
      {recentProducts.map(product => {
        const productInfoUrl = tR('/products') + '/' + product.get('url_slug');
        // Uses the current catalog price until dropship pricing is available in app data.
        const regularPrice = product.get('price').toDinero();
        const salePrice = isSaleProduct(product.get('id_product'))
          ? regularPrice.percentage(100 - SALE_DISCOUNT_PERCENT)
          : null;
        const regularPriceDisplay = regularPrice.toFormat();
        const displayPrice = salePrice?.toFormat() ?? regularPriceDisplay;

        return <RecentlyViewedProductLink
          key={product.get('id')}
          to={productInfoUrl}
          reloadDocument={!product.get('is_react_page')}
          onClick={() => onProductClick(product)}
        >
          <Stack direction="row" sx={{ minWidth: 0, width: '100%' }}>
            <RecentlyViewedImage>
              <ErrorBoundary fallback={<></>}>
                <ProductCatalogCardImage
                  slug={product.get('slug')}
                  name={product.get('name')}
                  sizes="72px"
                />
              </ErrorBoundary>
            </RecentlyViewedImage>
            <Stack spacing={0.25} sx={{ minWidth: 0, justifyContent: 'center', px: 1, }}>
              <RecentlyViewedProductName variant="h4">{product.get('name')}</RecentlyViewedProductName>
              <RecentlyViewedPrimaryPrice direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                {salePrice !== null ? <RecentlyViewedRegularPrice>{regularPriceDisplay}</RecentlyViewedRegularPrice> : null}
                {salePrice !== null
                  ? <RecentlyViewedSalePrice>{displayPrice}</RecentlyViewedSalePrice>
                  : <Typography variant="caption">{displayPrice}</Typography>}
              </RecentlyViewedPrimaryPrice>
            </Stack>
          </Stack>
        </RecentlyViewedProductLink>
      })}
    </RecentlyViewedProductsList>
  </RecentlyViewedSection>
}

export default memo(RecentlyViewedProducts);
